/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQueryClient } from "@tanstack/react-query";

import { Dispatch, JSX, SetStateAction, useReducer } from "react";
import { SubmitHandler, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ProfileForm } from "@/pages/gate/ProfilesPage";

import Button from "@/components/buttons/Button.tsx";
import { IconProfileLike } from "@/components/icons";
import UpdateProfileLayout from "@/components/layout/UpdateProfileLayout.tsx";

import { useGetProfileRecommendations } from "@/hooks/api/content.hooks.ts";
import { LOGIN_KEY } from "@/hooks/api/login.hooks";
import { useCreatePostProfile } from "@/hooks/api/user.hooks";
import { cn } from "@/utils/cn.ts";

import { ProfileTabs } from "../profiles/types";

interface State {
  content: string[];
  isError: boolean;
}

enum ActionType {
  ADD = "ADD",
  REMOVE = "REMOVE",
  TOGGLE_ERROR = "TOGGLE_ERROR",
}

type Action =
  | { type: ActionType.ADD; value: string }
  | { type: ActionType.REMOVE; value: string }
  | { type: ActionType.TOGGLE_ERROR };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case ActionType.ADD:
      return { content: [...state.content, action.value], isError: false };
    case ActionType.REMOVE:
      return { content: state.content.filter((item) => item !== action.value), isError: false };
    case ActionType.TOGGLE_ERROR:
      return { ...state, isError: true };
    default:
      return state;
  }
};

const ProfileRecommendationsTab = ({ setTab }: { setTab: Dispatch<SetStateAction<ProfileTabs>> }): JSX.Element => {
  const { t } = useTranslation();
  const { getValues, handleSubmit, setError } = useFormContext<ProfileForm>();
  const initialState: State = {
    content: getValues("selected_content") || [],
    isError: false,
  };
  const [state, dispatch] = useReducer(reducer, initialState);
  const { data } = useGetProfileRecommendations();

  const onBack = (): void => setTab(ProfileTabs.CONTENT_LANG);

  const { mutate } = useCreatePostProfile();
  const queryClient = useQueryClient();

  const handleClick: SubmitHandler<ProfileForm> = (data) => {
    const { picture, ...restData } = data;
    const picture_id = picture?.id ?? null;
    if (!restData.gender) {
      setError("gender", { message: t("error.need-gender") });
      return;
    }
    if (!restData.date_of_birth) {
      setError("date_of_birth", { type: "manual", message: t("error.need-date-of-birth") });
      return;
    }
    mutate(
      { data: { ...restData, picture_id, selected_content: state.content } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: LOGIN_KEY.profileList });
          setTab(ProfileTabs.PROFILES);
        },
        onError: (err: any) => {
          if (err.response.data.age) {
            setError("date_of_birth", {
              type: "manual",
              message: err.response.data.age,
            });
          } else if (err.response.data.gender) {
            setError("gender", {
              type: "manual",
              message: err.response.data.gender,
            });
            setTab(ProfileTabs.ADD_PROFILE);
          }
        },
      },
    );
  };

  return (
    <UpdateProfileLayout title={""} onBackClick={onBack} className={"py-40"}>
      <div className={"w-full max-w-[59.25rem] text-center"}>
        <h1 className={"text-[2rem]/10 font-semibold max-md:py-4 max-md:text-xl/6 max-md:font-bold"}>
          {t("Profile.choose-three-content")}
        </h1>
        <p className={"mt-4 max-md:mt-2"}>{t("Profile.choose-three-content-message")}</p>
        <div className={"mt-6 grid grid-cols-3 gap-6 max-md:grid-cols-2 max-md:gap-2"}>
          {data.map((item) => (
            <button
              key={item.id}
              aria-label={item.title}
              onClick={() => {
                if (state.content.includes(String(item.id))) {
                  dispatch({ type: ActionType.REMOVE, value: String(item.id) });
                } else {
                  dispatch({ type: ActionType.ADD, value: String(item.id) });
                }
              }}
              className={"relative"}
            >
              <img
                src={item.poster_h.small}
                alt={item.title}
                className={cn("aspect-video w-full rounded-5 transition-all max-md:rounded-[0.625rem]", {
                  "brightness-75": state.content.includes(String(item.id)),
                })}
              />

              <span
                className={cn(
                  "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform rounded-full bg-white/30 p-3 opacity-0",
                  {
                    "opacity-100": state.content.includes(String(item.id)),
                  },
                )}
              >
                <IconProfileLike className={"size-8 fill-on-primary"} />
              </span>
            </button>
          ))}
        </div>
        <div className={"mt-6 flex justify-end max-sm:sticky max-sm:bottom-4"}>
          <Button
            aria-label={
              state.content.length < 3
                ? t("Profile.choose-more-movies", { count: 3 - state.content.length })
                : t("Actions.save")
            }
            disabled={state.content.length < 3}
            onClick={handleSubmit(handleClick)}
            className={"w-full max-w-96 max-sm:max-w-none"}
          >
            {state.content.length < 3
              ? t("Profile.choose-more-movies", { count: 3 - state.content.length })
              : t("Actions.save")}
          </Button>
        </div>
      </div>
    </UpdateProfileLayout>
  );
};

export default ProfileRecommendationsTab;
